import React, { useState, useEffect } from 'react';

/**
 * Animated Population Counter Component
 * Shows numbers counting up/down with visual feedback during changes
 * Freezes during air strikes and nuclear strikes, then animates after completion
 * 
 * @param {number} value - Target population value
 * @param {string} color - Text color for display
 * @param {boolean} isLow - Whether population is critically low
 * @param {boolean} airStrikeActive - Whether air strike animation is active
 * @param {boolean} nuclearStrikeActive - Whether nuclear strike animation is active
 */
export default function AnimatedPopulation({ value, color, isLow, airStrikeActive, nuclearStrikeActive }) {
  const [displayValue, setDisplayValue] = useState(value);
  const [pendingValue, setPendingValue] = useState(null); // Store pending changes during animations
  const [isAnimating, setIsAnimating] = useState(false);
  const [direction, setDirection] = useState(null); // 'up' or 'down'

  useEffect(() => {
    // If air strike OR nuclear strike is active, FREEZE the display and store the pending value
    const isStrikeActive = airStrikeActive || nuclearStrikeActive;
    
    if (isStrikeActive && value !== displayValue) {
      console.log(`📊 Population change detected during strike - FREEZING at ${displayValue}M, pending: ${value}M`);
      setPendingValue(value);
      return; // Don't animate yet
    }
    
    // Strike just finished and we have a pending value - NOW animate!
    if (!isStrikeActive && pendingValue !== null) {
      console.log(`📊 Strike complete - Animating population from ${displayValue}M to ${pendingValue}M`);
      const targetValue = pendingValue;
      setPendingValue(null);
      
      const diff = targetValue - displayValue;
      setDirection(diff > 0 ? 'up' : 'down');
      setIsAnimating(true);

      // Animate the number changing
      const duration = 1000; // 1 second animation
      const steps = Math.abs(diff);
      const stepDuration = duration / Math.max(steps, 1);
      
      let current = displayValue;
      const increment = diff > 0 ? 1 : -1;
      
      const interval = setInterval(() => {
        current += increment;
        setDisplayValue(current);
        
        if ((diff > 0 && current >= targetValue) || (diff < 0 && current <= targetValue)) {
          clearInterval(interval);
          setDisplayValue(targetValue);
          setTimeout(() => setIsAnimating(false), 500);
        }
      }, stepDuration);

      return () => clearInterval(interval);
    }
    
    // Normal case (no strikes) - animate immediately if value changed
    if (!isStrikeActive && displayValue !== value && pendingValue === null) {
      const diff = value - displayValue;
      setDirection(diff > 0 ? 'up' : 'down');
      setIsAnimating(true);

      const duration = 1000;
      const steps = Math.abs(diff);
      const stepDuration = duration / Math.max(steps, 1);
      
      let current = displayValue;
      const increment = diff > 0 ? 1 : -1;
      
      const interval = setInterval(() => {
        current += increment;
        setDisplayValue(current);
        
        if ((diff > 0 && current >= value) || (diff < 0 && current <= value)) {
          clearInterval(interval);
          setDisplayValue(value);
          setTimeout(() => setIsAnimating(false), 500);
        }
      }, stepDuration);

      return () => clearInterval(interval);
    }
  }, [value, displayValue, airStrikeActive, nuclearStrikeActive, pendingValue]);

  return (
    <div 
      className={`font-mono font-bold text-base leading-none transition-all duration-300 relative ${
        isAnimating ? 'scale-125' : 'scale-100'
      }`}
      style={{
        color: isLow ? '#ef4444' : color,
        textShadow: `0 0 8px ${color}80, 0 2px 4px rgba(0,0,0,0.9)`,
        animation: isAnimating ? (direction === 'down' ? 'pulse-red 0.5s ease-in-out' : 'pulse-green 0.5s ease-in-out') : 'none',
      }}
    >
      {displayValue}M
      {isAnimating && (
        <span 
          className="absolute -top-2 -right-6 text-xs font-bold animate-bounce"
          style={{
            color: direction === 'down' ? '#ef4444' : '#22c55e',
            textShadow: '0 2px 4px rgba(0,0,0,0.9)',
          }}
        >
          {direction === 'down' ? '▼' : '▲'}
        </span>
      )}
    </div>
  );
}
